"use client";

/**
 * Full-page takeover rendered while the backend is in maintenance
 * mode. Driven by `useMaintenance()` - nothing else toggles it.
 *
 * Non-dismissible by design: the whole point of maintenance mode is
 * that API writes are paused, so letting the user "close" the screen
 * and click into an analysis that 503s immediately would be worse UX.
 *
 * Anatomy: wordmark, the scene on the paper tone, a plain heading that
 * names the fact, then the time: "about N minutes" leads and "back
 * around HH:MM" supports it, both from the 503's `until` (owner,
 * 2026-10-22: a time to wait, not just "we're improving"). Past the end
 * it says it is taking a little longer instead of counting below zero.
 * One sentence on why and on the user's data, then the quiet "last
 * checked HH:MM" line fed by the recovery probe (30 s cadence, see
 * maintenance-context).
 */

import { useEffect, useState } from "react";
import Illo from "@/components/Illo";
import { pluralForm } from "@/lib/announcements";
import type { TranslationKey } from "@/lib/i18n";
import { useT, useLocale } from "@/lib/locale-context";
import { useMaintenance } from "@/lib/maintenance-context";
import { maintenanceEta } from "@/lib/maintenance-eta";

export default function MaintenanceScreen() {
  const { inMaintenance, lastCheckedAt, until, reason } = useMaintenance();
  const t = useT();
  const { locale } = useLocale();
  // The minutes count down while the screen is up; a 15 s tick is
  // plenty for a minute display and stops with the screen.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!inMaintenance) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [inMaintenance]);

  if (!inMaintenance) return null;

  const clock = (d: Date) => d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const stamp = clock(new Date(lastCheckedAt ?? now));
  const eta = maintenanceEta(until, now);
  // The reason's own sentence; without one, the plain body (which
  // promises "a few minutes") only when no time was announced.
  const body = reason
    ? t(`maintenance.reason.${reason}` as TranslationKey)
    : eta.kind === "none" ? t("maintenance.body") : t("maintenance.saved");

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-live="assertive"
      // Stacked above every other UI surface (CookieBanner ~50,
      // VerifyEmailBanner ~40, MobileTabBar ~30). Using a very high
      // z-index is cheap here and keeps the takeover unambiguously
      // on top regardless of future layout work.
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-gray-50 px-6 py-8"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logo.svg"
            alt=""
            width={24}
            height={24}
            className="h-6 w-auto"
          />
          <span className="text-lg font-extrabold text-gray-900">
            {t("brand")}
          </span>
        </div>
        <div className="relative mt-5 h-44 w-full overflow-hidden rounded-2xl bg-[rgb(250,249,246)] dark:bg-[rgb(30,36,52)] sm:h-52">
          <Illo base="/illustrations/maintenance/upgrade" className="absolute inset-0 h-full w-full object-contain object-center p-2" />
        </div>
        <h1 className="mt-6 text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-[1.15]">
          {t("maintenance.title")}
        </h1>
        {eta.kind === "minutes" && (
          <div className="mt-3">
            <p className="text-xl font-extrabold text-gray-900 tabular-nums">
              {t(`maintenance.eta.${pluralForm(eta.minutes, locale)}` as TranslationKey).replace("{n}", String(eta.minutes))}
            </p>
            <p className="mt-0.5 text-sm text-gray-600 tabular-nums">
              {t("maintenance.back_at").replace("{time}", clock(eta.until))}
            </p>
          </div>
        )}
        {eta.kind === "overdue" && (
          <p className="mt-3 text-base font-semibold text-gray-900">
            {t("maintenance.overdue")}
          </p>
        )}
        <p className="mt-3 text-base text-gray-600 leading-relaxed">
          {body}
        </p>
        <p className="mt-5 text-xs text-gray-400 tabular-nums">
          {t("maintenance.lastcheck").replace("{time}", stamp)}
        </p>
      </div>
    </div>
  );
}
